import { getActivePinia } from "pinia";
import { useMergeTagsStore, type ISmartMergeTag } from "@/stores/mergeTags";

/**
 * Repairs colour-ish attribute values that arrive from outside the editor
 * (imported templates, Ecomail's automation). A hex saved as `##9E896F`
 * renders as invalid CSS, and Gmail then drops the whole `<style>` block it
 * sits in, taking the mobile column stacking with it. Only what can be fixed
 * without guessing is touched: repeated `#` collapse to one and surrounding
 * whitespace goes. Anything else is passed through unchanged and left to the
 * backend head-CSS sanitizer.
 */
export function normalizeColor<T>(value: T): T {
  if (typeof value !== "string") return value;
  return value.trim().replace(/#{2,}/g, "#") as T;
}

const HEX = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const KEYWORD = /^[a-z]+$/i; // named colours, transparent, inherit, currentColor
const JS_LEAK = /^(undefined|null|nan|object)$/i;
const FUNCTIONAL = /^(rgb|rgba|hsl|hsla)\(\s*[\d.%\s,/]+\)$/i;

type MergeTagSyntax = NonNullable<ISmartMergeTag["syntax"]>;
export type { MergeTagSyntax };

/**
 * A placeholder the customer's own system substitutes before send. The two
 * shapes the editor is used with are always recognised — `{{tag}}` (Topol /
 * Handlebars-style, `{{{tag}}}` included) and `*|TAG|*` (Ecomail) — and a
 * host that configured its own `smartMergeTags.syntax` (any two strings,
 * `<%tag%>` and the like) gets that shape on top: the defaults alone would
 * not recognise it and the `<` `>` of its tags would read as markup.
 * Deliberately unanchored: the tag may be the whole value, carry a `#`
 * prefix so the tag expands to bare hex digits (`#{{brand}}`), or sit inside
 * a function (`rgb({{r}},{{g}},{{b}})`).
 *
 * The triple-brace form is tried first so `{{{tag}}}` is consumed whole; the
 * other order would leave a stray `}` behind and the value would be rejected.
 * Kept in step with the backend head-CSS sanitizer, which masks the same
 * shapes before parsing and receives the same host syntax with the request.
 */
const DEFAULT_MERGE_TAG =
  /\{\{\{[\s\S]*?\}\}\}|\{\{[\s\S]*?\}\}|\*\|[^|]*\|\*/g;

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

let cachedSyntax: MergeTagSyntax | null = null;
let cachedPattern = DEFAULT_MERGE_TAG;

/**
 * The merge-tag shapes to strip before judging a colour: the defaults, plus
 * the host's own `smartMergeTags.syntax` when it configured one. Read from
 * the store on every call because the colour check runs inside
 * `TopolColorSchema`, a module-level zod schema that cannot be handed the
 * syntax any other way. Without an active pinia (unit tests parsing the
 * schemas on their own) only the defaults apply. A configured pair matches
 * lazily from `start` to the nearest `end`.
 */
function mergeTagPattern(): RegExp {
  const syntax =
    (getActivePinia() ? useMergeTagsStore().hostMTSyntax : null) ?? null;
  if (syntax === cachedSyntax) return cachedPattern;

  cachedSyntax = syntax;
  if (!syntax || syntax.start === "" || syntax.end === "") {
    cachedPattern = DEFAULT_MERGE_TAG;
  } else {
    cachedPattern = new RegExp(
      `${DEFAULT_MERGE_TAG.source}|${escapeRegExp(syntax.start)}[\\s\\S]*?${escapeRegExp(syntax.end)}`,
      "g"
    );
  }
  return cachedPattern;
}

/**
 * What may legitimately surround a merge tag in a colour value: a `#`
 * prefix, a colour function's name and brackets, separators, numbers and
 * units. Everything else is rejected — `;` would start a second declaration
 * (`#{{brand}}; width: 10px`), `<` `>` `{` `}` would end the `<style>` block
 * the colour is rendered into, quotes and `\\` would break the attribute.
 */
const AROUND_TAG = /^[#\w\s(),.%/]*$/;

function hasBalancedParens(text: string): boolean {
  let depth = 0;
  for (const ch of text) {
    if (ch === "(") depth++;
    else if (ch === ")" && --depth < 0) return false;
  }
  return depth === 0;
}

/**
 * Is the (already normalized) value something a colour attribute may hold?
 * Empty string counts: the editor stores "" for "not set". Named colours are
 * not checked against the CSS list — a plain word cannot break a stylesheet.
 *
 * A value carrying a merge tag is opaque until the customer's system fills it
 * in, so only what SURROUNDS the tag is judged, and as strictly as a plain
 * value: the remainder must be made of colour characters only and its
 * brackets must pair up. A hover colour of `#{{a}}</style><script>…` would
 * otherwise ride into the rendered head CSS and close the `<style>` element
 * early — the backend sanitizer cannot catch that one, its block regex ends
 * at the injected `</style>` — and `#{{a}}; width: 10px` would smuggle a
 * second declaration through a colour-only field.
 */
export function isCssColor(value: string): boolean {
  const withoutTags = value.replace(mergeTagPattern(), "");
  if (withoutTags !== value) {
    return AROUND_TAG.test(withoutTags) && hasBalancedParens(withoutTags);
  }

  return (
    value === "" ||
    HEX.test(value) ||
    (KEYWORD.test(value) && !JS_LEAK.test(value)) ||
    FUNCTIONAL.test(value)
  );
}
